// DeveloperAudioPlayer.tsx - CORS WORKAROUND VERSION with VOLUME CONTROL & SMOOTH DRAGGING
// Container height now matches Acknowledge & Unclear buttons

import React, { useState, useRef, useEffect, useCallback } from "react";
import { Trash2, Volume2, VolumeX } from "lucide-react";
import { ReactComponent as PlayRecording } from "../../assets/icons/svg/play-recording.svg";
import { ReactComponent as PauseRecording } from "../../assets/icons/svg/puse-recording.svg";
import { BASE_URL } from "../../api/urls";
import { DEFAULT_FONT_FAMILY } from "../../config/fontConfig";

interface DeveloperAudioPlayerProps {
  audioUrl: string;
  onError?: (error: string) => void;
  onDelete?: () => void;
  className?: string;
}

export const DeveloperAudioPlayer: React.FC<DeveloperAudioPlayerProps> = ({
  audioUrl,
  onError,
  onDelete,
  className = "",
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [blobUrl, setBlobUrl] = useState<string | null>(null);
  const [progress, setProgress] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [showVolumeSlider, setShowVolumeSlider] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const audioRef = useRef<HTMLAudioElement>(null);
  const waveformRef = useRef<HTMLDivElement | null>(null);
  const volumeRef = useRef<HTMLDivElement>(null);
  const volumeSliderRef = useRef<HTMLDivElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);

  // Convert relative URL to absolute URL
  const getFullAudioUrl = (url: string): string => {
    if (!url) return "";
    if (url.startsWith("http://") || url.startsWith("https://")) return url;
    if (url.startsWith("/")) return `${BASE_URL}${url}`;
    return `${BASE_URL}/${url}`;
  };

  const fullAudioUrl = getFullAudioUrl(audioUrl);

  // Fetch audio as blob to bypass CORS
  useEffect(() => {
    if (!fullAudioUrl) {
      setError("Invalid audio URL");
      setIsLoading(false);
      onError?.("Invalid audio URL");
      return;
    }

    let isMounted = true;
    const abortController = new AbortController();

    const fetchAudioAsBlob = async () => {
      try {
        setIsLoading(true);
        setError(null);

        console.log("🎵 Fetching audio as blob:", fullAudioUrl);

        const response = await fetch(fullAudioUrl, {
          signal: abortController.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const blob = await response.blob();

        if (!isMounted) return;

        const objectUrl = URL.createObjectURL(blob);
        setBlobUrl(objectUrl);
        setIsLoading(false);

        console.log("✅ Audio blob loaded successfully");
      } catch (err: any) {
        if (err.name === "AbortError") return;

        console.error("❌ Failed to fetch audio:", err);

        if (isMounted) {
          setError(`Failed to load audio: ${err.message}`);
          setIsLoading(false);
          onError?.(err.message);
        }
      }
    };

    fetchAudioAsBlob();

    return () => {
      isMounted = false;
      abortController.abort();

      if (blobUrl) {
        URL.revokeObjectURL(blobUrl);
      }
    };
  }, [fullAudioUrl]);

  // Apply volume changes to audio element
  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = volume;
      audioRef.current.muted = isMuted;
    }
  }, [volume, isMuted]);

  // Smooth progress update using requestAnimationFrame
  useEffect(() => {
    if (!audioRef.current || isDragging || !isPlaying) return;

    let animationFrameId: number | null = null;

    const updateProgress = () => {
      if (audioRef.current && !isDragging && isPlaying) {
        const currentTime = audioRef.current.currentTime;
        const duration = audioRef.current.duration;
        const progressValue = duration > 0 ? currentTime / duration : 0;

        setCurrentTime(currentTime);
        setProgress(progressValue);

        animationFrameId = requestAnimationFrame(updateProgress);
      }
    };

    animationFrameId = requestAnimationFrame(updateProgress);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [isDragging, isPlaying]);

  // Audio event handlers
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !blobUrl) return;

    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      setCurrentTime(audio.currentTime);
    };

    const handleCanPlay = () => {
      console.log("✅ Audio ready to play");
    };

    const handleTimeUpdate = () => {
      // Fallback for paused state
      if (!isDragging && !isPlaying) {
        const currentTime = audio.currentTime;
        const duration = audio.duration;
        const progressValue = duration > 0 ? currentTime / duration : 0;

        setCurrentTime(currentTime);
        setProgress(progressValue);
      }
    };

    const handleEnded = () => {
      setIsPlaying(false);
      setCurrentTime(0);
      setProgress(0);
      audio.currentTime = 0;
    };

    const handleError = (e: Event) => {
      const target = e.target as HTMLAudioElement;
      const mediaError = target.error;

      let errorMsg = "Failed to play audio";
      if (mediaError) {
        switch (mediaError.code) {
          case MediaError.MEDIA_ERR_ABORTED:
            errorMsg = "Audio playback aborted";
            break;
          case MediaError.MEDIA_ERR_NETWORK:
            errorMsg = "Network error during playback";
            break;
          case MediaError.MEDIA_ERR_DECODE:
            errorMsg = "Audio decoding error";
            break;
          case MediaError.MEDIA_ERR_SRC_NOT_SUPPORTED:
            errorMsg = "Audio format not supported";
            break;
        }
      }

      console.error("❌ Audio error:", errorMsg);
      setError(errorMsg);
      onError?.(errorMsg);
    };

    audio.addEventListener("loadedmetadata", handleLoadedMetadata);
    audio.addEventListener("canplay", handleCanPlay);
    audio.addEventListener("timeupdate", handleTimeUpdate);
    audio.addEventListener("ended", handleEnded);
    audio.addEventListener("error", handleError);

    return () => {
      audio.removeEventListener("loadedmetadata", handleLoadedMetadata);
      audio.removeEventListener("canplay", handleCanPlay);
      audio.removeEventListener("timeupdate", handleTimeUpdate);
      audio.removeEventListener("ended", handleEnded);
      audio.removeEventListener("error", handleError);
    };
  }, [blobUrl, onError, isDragging, isPlaying]);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio
        .play()
        .then(() => {
          setIsPlaying(true);
        })
        .catch((err) => {
          console.error("❌ Playback error:", err);
          setError("Failed to play audio");
          onError?.("Failed to play audio");
        });
    }
  };

  // Progress bar dragging functionality
  const handleProgressBarMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!audioRef.current || !progressBarRef.current) return;

      e.preventDefault();
      e.stopPropagation();

      const wasPlaying = isPlaying;
      if (wasPlaying && audioRef.current) {
        audioRef.current.pause();
      }

      setIsDragging(true);

      const updateProgress = (clientX: number) => {
        if (!progressBarRef.current || !audioRef.current) return;

        const rect = progressBarRef.current.getBoundingClientRect();
        const x = clientX - rect.left;
        const percentage = Math.max(0, Math.min(1, x / rect.width));
        const newTime = percentage * audioRef.current.duration;

        audioRef.current.currentTime = newTime;
        setProgress(percentage);
        setCurrentTime(newTime);
      };

      updateProgress(e.clientX);

      const handleMouseMove = (moveEvent: MouseEvent) => {
        updateProgress(moveEvent.clientX);
      };

      const handleMouseUp = () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
        document.body.style.userSelect = "";
        document.body.style.cursor = "";

        setIsDragging(false);

        if (wasPlaying && audioRef.current) {
          audioRef.current.play();
        }
      };

      document.body.style.userSelect = "none";
      document.body.style.cursor = "pointer";
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    },
    [isPlaying],
  );

  // Volume controls
  const toggleVolumeSlider = useCallback(() => {
    console.log("Volume slider toggle clicked:", !showVolumeSlider);
    setShowVolumeSlider(!showVolumeSlider);
  }, [showVolumeSlider]);

  const handleVolumeSliderMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const updateVolume = (clientY: number) => {
      if (!volumeSliderRef.current) return;

      const rect = volumeSliderRef.current.getBoundingClientRect();
      const y = clientY - rect.top;
      const percentage = Math.max(0, Math.min(1, 1 - y / rect.height));

      setVolume(percentage);
      setIsMuted(percentage === 0);
    };

    updateVolume(e.clientY);

    const handleMouseMove = (moveEvent: MouseEvent) => {
      updateVolume(moveEvent.clientY);
    };

    const handleMouseUp = () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
      document.body.style.userSelect = "";
    };

    document.body.style.userSelect = "none";
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  }, []);

  // Click outside handler for volume slider
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        showVolumeSlider &&
        volumeRef.current &&
        !volumeRef.current.contains(event.target as Node)
      ) {
        setShowVolumeSlider(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showVolumeSlider]);

  const formatTime = (seconds: number): string => {
    if (!seconds || isNaN(seconds)) return "0s";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return mins > 0
      ? `${mins}:${secs.toString().padStart(2, "0")}`
      : `${secs}s`;
  };

  // Loading state
  if (isLoading) {
    return (
      <div className={`w-full ${className}`}>
        <div className="flex items-center gap-3">
          <div className="flex-1 h-10 xl:h-12 3xl:h-14 4xl:h-16 bg-[#F4F5F6] rounded-[8px] flex items-center px-3 overflow-hidden relative">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 animate-spin">
                <svg className="w-full h-full" fill="none" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              </div>
              <span
                className="text-sm text-gray-600"
                style={{ fontFamily: DEFAULT_FONT_FAMILY }}
              >
                Loading audio...
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className={`w-full ${className}`}>
        <div className="flex items-center gap-3">
          <div className="flex-1 h-10 xl:h-12 3xl:h-14 4xl:h-16 bg-red-50 border border-red-200 rounded-[8px] flex items-center px-3 overflow-hidden relative">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 text-red-500">⚠️</div>
              <span
                className="text-sm text-red-700"
                style={{ fontFamily: DEFAULT_FONT_FAMILY }}
              >
                {error}
              </span>
            </div>
          </div>

          {onDelete && (
            <button
              onClick={onDelete}
              className="h-10 xl:h-12 3xl:h-14 4xl:h-16 w-10 xl:w-12 3xl:w-14 4xl:w-16 flex items-center justify-center transition-opacity hover:opacity-80"
              aria-label="Delete recording"
            >
              <Trash2 className="w-5 h-5 xl:w-6 xl:h-6 3xl:w-7 3xl:h-7 4xl:w-8 4xl:h-8 text-red-600" />
            </button>
          )}
        </div>

        <div className="mt-2 flex gap-2">
          <button
            onClick={() => {
              setError(null);
              setIsLoading(true);
              setBlobUrl(null);
            }}
            className="text-xs px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
            style={{ fontFamily: DEFAULT_FONT_FAMILY }}
          >
            Retry
          </button>
          <a
            href={fullAudioUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-xs px-3 py-1 bg-gray-600 text-white rounded hover:bg-gray-700"
            style={{ fontFamily: DEFAULT_FONT_FAMILY }}
          >
            Open File
          </a>
        </div>
      </div>
    );
  }

  // Main audio player UI
  return (
    <div className={`w-full ${className} relative`}>
      {blobUrl && (
        <audio
          ref={audioRef}
          src={blobUrl}
          preload="metadata"
          style={{ display: "none" }}
        />
      )}

      <div className="flex items-center gap-3">
        <div
          ref={waveformRef}
          className="flex-1 h-10 xl:h-12 3xl:h-14 4xl:h-16 bg-[#F4F5F6] rounded-[8px] flex items-center px-3 overflow-visible relative"
        >
          {/* Play/Pause Button */}
          <button
            onClick={togglePlay}
            className="text-indigo-600 flex items-center justify-center mr-4 transition-colors hover:text-indigo-700"
            aria-label={isPlaying ? "Pause recording" : "Play recording"}
          >
            {isPlaying ? (
              <PauseRecording className="w-5 h-5 xl:w-6 xl:h-6 3xl:w-7 3xl:h-7 4xl:w-8 4xl:h-8" />
            ) : (
              <PlayRecording className="w-5 h-5 xl:w-6 xl:h-6 3xl:w-7 3xl:h-7 4xl:w-8 4xl:h-8 ml-0.5" />
            )}
          </button>

          {/* Progress Bar */}
          <div className="flex-1 relative flex items-center mr-4">
            <div
              ref={progressBarRef}
              className="flex-1 h-1 bg-gray-300 rounded-full relative overflow-visible cursor-pointer"
              onMouseDown={handleProgressBarMouseDown}
            >
              <div
                className="h-1 bg-[#1E1F26] rounded-full"
                style={{
                  width: `${progress * 100}%`,
                }}
              />
              <div
                className={`absolute top-1/2 transform -translate-y-1/2 w-4 h-4 bg-[#1E1F26] border-4 border-[#B4B5B8] border-opacity-100 rounded-full shadow-sm transition-transform ${
                  isDragging ? "scale-110" : "hover:scale-110"
                }`}
                style={{
                  left: `${progress * 100}%`,
                  marginLeft: "-8px",
                }}
              />
            </div>
          </div>

          {/* Volume Control */}
          <div
            className="flex items-center space-x-2 relative z-50"
            ref={volumeRef}
          >
            <button
              onClick={toggleVolumeSlider}
              className="w-8 h-8 flex items-center justify-center text-gray-600 hover:text-gray-800 transition-colors relative z-50"
              aria-label="Volume control"
            >
              {isMuted || volume === 0 ? (
                <VolumeX className="w-4 h-4" />
              ) : (
                <Volume2 className="w-4 h-4" />
              )}
            </button>

            {/* Volume Slider Popup */}
            {showVolumeSlider && (
              <div className="absolute bottom-full mb-3 -right-2 bg-white border border-gray-200 rounded-lg shadow-xl p-3 min-w-[40px] z-[100]">
                <div className="flex flex-col items-center space-y-2">
                  <div className="text-xs text-gray-600 font-medium">
                    {Math.round(volume * 100)}
                  </div>
                  <div
                    ref={volumeSliderRef}
                    className="relative h-20 w-6 flex items-center justify-center cursor-pointer select-none"
                    onMouseDown={handleVolumeSliderMouseDown}
                  >
                    <div className="w-1 h-full bg-gray-300 rounded-full relative">
                      <div
                        className="absolute bottom-0 w-full bg-[#1E1F26] rounded-full transition-all duration-75 ease-out"
                        style={{ height: `${volume * 100}%` }}
                      />
                    </div>
                    <div
                      className="absolute w-3 h-3 bg-[#1E1F26] rounded-full shadow-sm pointer-events-none transition-all duration-75 ease-out hover:scale-110"
                      style={{
                        bottom: `${volume * 100}%`,
                        left: "50%",
                        transform: "translateX(-50%) translateY(50%)",
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Delete Button - Only show if onDelete callback is provided */}
        {onDelete && (
          <button
            onClick={onDelete}
            className="h-10 xl:h-12 3xl:h-14 4xl:h-16 w-10 xl:w-12 3xl:w-14 4xl:w-16 flex items-center justify-center transition-opacity hover:opacity-80"
            aria-label="Delete recording"
            title="Delete recording"
          >
            <Trash2 className="w-5 h-5 xl:w-6 xl:h-6 3xl:w-7 3xl:h-7 4xl:w-8 4xl:h-8 text-red-600" />
          </button>
        )}
      </div>
    </div>
  );
};
